import React, { createContext, useContext, useState, ReactNode, useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { selectDealerSelected } from 'app/pages/Header/selectors';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import { sliceKey, reducer } from 'app/pages/Header/slice';
import { headerSaga } from 'app/pages/Header/saga';
import SummarizationApiService, { SummarizationInitResponse } from 'app/apiService/summarization/summarizationApiService';
import { SummarizationSocketService } from 'app/socketService/summarization/salesPortalSocketService';
import socketServiceManager from 'app/socketService';

// ----------- Type Definitions -----------
interface SummarizationContextType {
  loading: boolean;
  error: string | null;
  conversation_id: string | null;
  streamingText: string; // Accumulated streaming text
  isStreaming: boolean; // Whether streaming is in progress
  init: () => Promise<SummarizationInitResponse | null>;
  setConversationId: (id: string) => void; // Method to set conversation_id externally
  clearError: () => void;
  resetState: () => void;
  clearStreamingText: () => void; // Method to clear only streaming text
}

// ----------- Context Create -----------
const SummarizationContext = createContext<SummarizationContextType | undefined>(undefined);

// ----------- Provider Component -----------
export const SummarizationProvider: React.FC<{ children: ReactNode; dealerId?: string }> = ({ children, dealerId: propDealerId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conversation_id, setConversationId] = useState<string | null>(null);
  const [streamingText, setStreamingText] = useState<string>('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [socketConnected, setSocketConnected] = useState(false);
  
  // Refs for socket service
  const socketServiceRef = useRef<SummarizationSocketService | null>(null);
  const isInitializedRef = useRef(false);
  const subscriptionIdRef = useRef<string | null>(null);
  const lastProcessedTextLengthRef = useRef<number>(0); // Track last processed text length to prevent duplicates
  const accumulatedTextRef = useRef<string>(''); // Track accumulated text to prevent duplicates
  const subscriptionCallbackRef = useRef<((data: any) => void) | null>(null); // Track subscription callback to prevent duplicates

  // Inject Header reducer and saga for dealer functionality (only if not using prop dealerId)
  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: headerSaga });

  // Get selected dealer from header state (fallback if propDealerId not provided)
  const selectedDealer = useSelector(selectDealerSelected);
  const currentDealerId = propDealerId || selectedDealer?.dealer || '';

  // Initialize socket service
  useEffect(() => {
    if (isInitializedRef.current) return;
    
    const socketConfig = {
      url: process.env.REACT_APP_API_URL || process.env.REACT_APP_WS_URL || 'http://localhost:3000',
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    };

    try {
      // Initialize global socket connection
      socketServiceManager.initializeGlobalSocket(socketConfig);
      
      // Create and register Summarization service
      const summarizationService = SummarizationSocketService.getInstance(socketConfig);
      socketServiceManager.registerService(summarizationService);
      socketServiceRef.current = summarizationService;
      
      // Set up connection event listeners
      summarizationService.on('connect', () => {
        setSocketConnected(true);
        console.log('📡 Summarization socket connected');
      });

      summarizationService.on('disconnect', (reason) => {
        setSocketConnected(false);
        console.log('📡 Summarization socket disconnected:', reason);
      });

      summarizationService.on('connect_error', (error) => {
        setSocketConnected(false);
        console.error('❌ Summarization socket connection error:', error);
      });

      summarizationService.on('reconnect', (attemptNumber) => {
        setSocketConnected(true);
        console.log('📡 Summarization socket reconnected:', attemptNumber);
      });

      // Connect to socket
      summarizationService.connect();

      isInitializedRef.current = true;
      console.log('📡 Summarization socket service initialized');
    } catch (error) {
      console.error('❌ Failed to initialize summarization socket service:', error);
      setError('Failed to initialize socket service');
    }

    // Cleanup function
    return () => {
      try {
        if (socketServiceRef.current) {
          socketServiceRef.current.disconnect();
        }
      } catch (error) {
        console.error('❌ Error disconnecting summarization socket service:', error);
      }
    };
  }, []);

  // Create a single reusable callback for streaming events
  const createStreamingCallback = useCallback((conversationId: string) => {
    return (data: any) => {
      console.log('📊 Summarization event received:', data);
      
      // Handle streaming_response events
      if (data.event_type === 'streaming_response' || data.message) {
        // Extract message - could be nested in data.message or be data itself
        const messageObj = typeof data.message === 'object' && data.message !== null 
          ? data.message 
          : (typeof data === 'object' && data !== null ? data : null);
        
        // Check if this is a streaming response with proper structure
        if (
          messageObj && 
          typeof messageObj === 'object' &&
          'conversationId' in messageObj &&
          'text' in messageObj &&
          messageObj.conversationId === conversationId &&
          typeof messageObj.text === 'string'
        ) {
          const newTextChunk = messageObj.text as string;
          
          if (!newTextChunk || newTextChunk.length === 0) {
            return; // Skip empty chunks
          }
          
          const currentAccumulated = accumulatedTextRef.current;
          
          // Smart deduplication: Check if the new chunk is already at the end
          if (currentAccumulated.endsWith(newTextChunk)) {
            console.log('⚠️ Duplicate chunk detected (ends with), skipping');
            return;
          }
          
          // Check for overlap: new chunk might start with text we already have
          // This handles cases where backend sends overlapping chunks
          let textToAppend = newTextChunk;
          
          if (currentAccumulated.length > 0) {
            // Find the longest overlap at the end of accumulated and start of new chunk
            const maxOverlapLength = Math.min(currentAccumulated.length, newTextChunk.length);
            
            for (let i = maxOverlapLength; i > 0; i--) {
              const endOfAccumulated = currentAccumulated.slice(-i);
              const startOfNewChunk = newTextChunk.slice(0, i);
              
              if (endOfAccumulated === startOfNewChunk) {
                // Found overlap, only append the non-overlapping part
                textToAppend = newTextChunk.slice(i);
                break;
              }
            }
          }
          
          // Only append if there's new text
          if (textToAppend.length > 0) {
            accumulatedTextRef.current = currentAccumulated + textToAppend;
            setStreamingText(accumulatedTextRef.current);
          } else {
            console.log('⚠️ No new text to append after deduplication');
          }
          
          setIsStreaming(true);
          
          // If isFinal is true, mark streaming as complete
          if (messageObj.isFinal === true) {
            setIsStreaming(false);
            console.log('✅ Streaming completed');
          }
        }
      }
    };
  }, []);

  const init = useCallback(async (): Promise<SummarizationInitResponse | null> => {
    if (!currentDealerId) {
      setError('No dealer selected');
      return null;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await SummarizationApiService.init(currentDealerId);

      if (response.body) {
        setLoading(false)
        console.log('Init response received, conversation_id:', response.body.conversation_id);
        
        // Set conversation_id first, even if socket service fails
        setConversationId(response.body.conversation_id);
        // Reset streaming text for new conversation
        setStreamingText('');
        accumulatedTextRef.current = '';
        lastProcessedTextLengthRef.current = 0;
        console.log('conversation_id set in context:', response.body.conversation_id);
        
        // Try to subscribe to socket service, but don't fail if it errors
        try {
          const service = socketServiceRef.current || socketServiceManager.getService<SummarizationSocketService>('SummarizationSocketService');
          
          if (service) {
            // Check if already subscribed to this conversation
            if (subscriptionIdRef.current === response.body.conversation_id && subscriptionCallbackRef.current) {
              console.log('📡 Already subscribed to this conversation');
              return response.body;
            }
            
            // Create callback once
            const callback = createStreamingCallback(response.body.conversation_id);
            subscriptionCallbackRef.current = callback;
            
            // Ensure service is connected before subscribing
            if (!service.isConnected()) {
              console.log('📡 Socket not connected, attempting to connect...');
              service.connect();
              
              // Wait a bit for connection, then subscribe
              setTimeout(() => {
                if (service.isConnected()) {
                  subscriptionIdRef.current = response.body.conversation_id;
                  service.subscribeToSummarization(response.body.conversation_id, callback, {
                    conversation_id: response.body.conversation_id,
                    subscription_type: 'conversation'
                  });
                  console.log('📡 Subscribed to summarization events for conversation:', response.body.conversation_id);
                } else {
                  console.warn('⚠️ Socket still not connected after timeout, subscription will be queued');
                  // Still try to subscribe - the service will queue it
                  subscriptionIdRef.current = response.body.conversation_id;
                  service.subscribeToSummarization(response.body.conversation_id, callback, {
                    conversation_id: response.body.conversation_id,
                    subscription_type: 'conversation'
                  });
                }
              }, 500);
            } else {
              // Socket is already connected, subscribe immediately
              subscriptionIdRef.current = response.body.conversation_id;
              service.subscribeToSummarization(response.body.conversation_id, callback, {
                conversation_id: response.body.conversation_id,
                subscription_type: 'conversation'
              });
              console.log('📡 Subscribed to summarization events for conversation:', response.body.conversation_id);
            }
          } else {
            console.warn('⚠️ Summarization socket service not available');
          }
        } catch (socketError: any) {
          console.warn('⚠️ Socket service subscription failed, but conversation_id is set:', socketError);
          // Don't throw - conversation_id is already set, so we can continue
        }
        
        return response.body;
      } else {
        setError('Failed to initialize summarization');
        return null;
      }
    } catch (err: any) {
      const errorMessage = err?.message || 'An error occurred while initializing summarization';
      setError(errorMessage);
      console.error('Init summarization error:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [currentDealerId, createStreamingCallback]);

  // Subscribe to socket when conversation_id is available (from external init or context init)
  useEffect(() => {
    if (conversation_id) {
      // Subscribe to socket service when conversation_id is available
      const service = socketServiceRef.current || socketServiceManager.getService<SummarizationSocketService>('SummarizationSocketService');
      
      if (!service) {
        console.warn('⚠️ Socket service not available for subscription');
        return;
      }
      
      // Check if already subscribed to this conversation
      if (subscriptionIdRef.current === conversation_id && subscriptionCallbackRef.current) {
        console.log('📡 Already subscribed to conversation:', conversation_id);
        return;
      }
      
      // Ensure socket is connected before subscribing
      if (!service.isConnected()) {
        console.log('📡 Socket not connected, attempting to connect...');
        service.connect();
        
        // Wait for connection, then subscribe
        const connectHandler = () => {
          console.log('📡 Socket connected, now subscribing...');
          const callback = createStreamingCallback(conversation_id);
          subscriptionCallbackRef.current = callback;
          subscriptionIdRef.current = conversation_id;
          service.subscribeToSummarization(conversation_id, callback, {
            conversation_id: conversation_id,
            subscription_type: 'conversation'
          });
          console.log('📡 Subscribed to conversation:', conversation_id);
          service.off('connect', connectHandler);
        };
        
        service.on('connect', connectHandler);
        
        // Also try subscribing immediately in case it's already connected
        setTimeout(() => {
          if (service.isConnected() && !subscriptionCallbackRef.current) {
            const callback = createStreamingCallback(conversation_id);
            subscriptionCallbackRef.current = callback;
            subscriptionIdRef.current = conversation_id;
            service.subscribeToSummarization(conversation_id, callback, {
              conversation_id: conversation_id,
              subscription_type: 'conversation'
            });
            console.log('📡 Subscribed to conversation (after timeout):', conversation_id);
            service.off('connect', connectHandler);
          }
        }, 500);
      } else {
        // Socket is already connected, subscribe immediately
        const callback = createStreamingCallback(conversation_id);
        subscriptionCallbackRef.current = callback;
        subscriptionIdRef.current = conversation_id;
        service.subscribeToSummarization(conversation_id, callback, {
          conversation_id: conversation_id,
          subscription_type: 'conversation'
        });
        console.log('📡 Subscribed to conversation (socket already connected):', conversation_id);
      }
    }
  }, [conversation_id, createStreamingCallback]);

  const setConversationIdExternal = useCallback((id: string) => {
    setConversationId(id);
    setStreamingText('');
    accumulatedTextRef.current = '';
    lastProcessedTextLengthRef.current = 0;
    console.log('📊 Conversation ID set externally:', id);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const clearStreamingText = useCallback(() => {
    console.log('🔄 Clearing streaming text...');
    setStreamingText('');
    accumulatedTextRef.current = '';
    lastProcessedTextLengthRef.current = 0;
    setIsStreaming(false);
    console.log('✅ Streaming text cleared');
  }, []);

  const resetState = useCallback(() => {
    console.log('🔄 Resetting summarization states...');
    
    // Unsubscribe from socket service if subscribed
    if (subscriptionIdRef.current && socketServiceRef.current) {
      try {
        socketServiceRef.current.unsubscribeFromSalesPortal(subscriptionIdRef.current);
        console.log('📡 Unsubscribed from summarization:', subscriptionIdRef.current);
      } catch (error) {
        console.error('❌ Error unsubscribing:', error);
      }
    }
    
    // Reset all states
    setConversationId(null);
    setError(null);
    setStreamingText('');
    setIsStreaming(false);
    subscriptionIdRef.current = null;
    lastProcessedTextLengthRef.current = 0;
    accumulatedTextRef.current = '';
    subscriptionCallbackRef.current = null;
    
    console.log('✅ All summarization states reset');
  }, []);

  const contextValue: SummarizationContextType = {
    loading,
    error,
    conversation_id,
    streamingText,
    isStreaming,
    init,
    setConversationId: setConversationIdExternal,
    clearError,
    resetState,
    clearStreamingText,
  };

  return (
    <SummarizationContext.Provider value={contextValue}>
      {children}
    </SummarizationContext.Provider>
  );
};

// ----------- Custom Hook -----------
export const useSummarization = (): SummarizationContextType => {
  const context = useContext(SummarizationContext);
  if (!context) {
    throw new Error('useSummarization must be used within a SummarizationProvider');
  }
  return context;
};

export default SummarizationContext;
